import { useEffect, useState } from "react";
import { Plus, Trash2, ClipboardList, CheckCircle2, Paperclip } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { fecha } from "@/lib/format";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function DocenteTareas() {
  const [tareas, setTareas] = useState(null);
  const [materias, setMaterias] = useState([]);
  const [open, setOpen] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [notasEntrega, setNotasEntrega] = useState({});
  const [form, setForm] = useState({
    materia_id: "", titulo: "", descripcion: "", fecha_entrega: "",
    archivo_nombre: "", archivo_base64: "",
  });

  const cargar = () => {
    api.get("/docente/tareas").then((r) => setTareas(r.data)).catch(() => setTareas([]));
    api.get("/docente/materias").then((r) => setMaterias(r.data));
  };

  useEffect(cargar, []);

  const crear = async () => {
    if (!form.materia_id) return toast.error("Seleccione la materia");
    if (!form.titulo.trim()) return toast.error("Indique el título de la actividad");
    if (!form.fecha_entrega) return toast.error("Indique la fecha de entrega");
    try {
      await api.post("/docente/tareas", { ...form, fecha_entrega: new Date(form.fecha_entrega).toISOString() });
      toast.success("Actividad publicada para los estudiantes");
      setOpen(false);
      setForm({ materia_id: "", titulo: "", descripcion: "", fecha_entrega: "",
                archivo_nombre: "", archivo_base64: "" });
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const adjuntar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("El archivo no debe superar los 5 MB");
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, archivo_nombre: file.name, archivo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const eliminar = async (id) => {
    try {
      await api.delete(`/docente/tareas/${id}`);
      toast.success("Actividad eliminada");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const verEntregas = async (t) => {
    const { data } = await api.get(`/docente/tareas/${t.id}/entregas`);
    setDetalle(data);
  };

  const evaluar = async (entregaId) => {
    const nota = notasEntrega[entregaId];
    if (nota === undefined || nota === "" || Number.isNaN(Number(nota))) {
      return toast.error("Indique la nota de la entrega");
    }
    try {
      await api.post(`/docente/entregas/${entregaId}/evaluar`, { nota: Number(nota) });
      toast.success("Entrega evaluada");
      const { data } = await api.get(`/docente/tareas/${detalle.tarea.id}/entregas`);
      setDetalle(data);
      setNotasEntrega({});
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!tareas) return <Loading />;

  return (
    <div data-testid="docente-tareas">
      <PageTitle title="Tareas y actividades"
                 subtitle="Publique asignaciones con fecha límite en sus materias y evalúe las entregas.">
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="nueva-tarea-docente"
                disabled={materias.length === 0}>
          <Plus className="mr-2 h-4 w-4" /> Nueva actividad
        </Button>
      </PageTitle>

      {tareas.length === 0 ? (
        <Empty testId="sin-tareas-docente">
          {materias.length === 0
            ? "Aún no tiene materias asignadas."
            : "No ha publicado actividades todavía."}
        </Empty>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2" data-testid="lista-tareas-docente">
          {tareas.map((t) => (
            <article key={t.id} className="lift rounded-lg border border-border/60 bg-card p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {t.materia_nombre} · {t.grado}
                  </p>
                  <h3 className="mt-2 font-display text-lg font-semibold">{t.titulo}</h3>
                </div>
                <span className="flex flex-col items-end gap-2">
                  <Badge variant="outline" className="font-mono text-[10px]">{fecha(t.fecha_entrega)}</Badge>
                  {t.origen === "classroom" && (
                    <Badge variant="secondary" data-testid={`origen-classroom-${t.id}`}>Classroom</Badge>
                  )}
                </span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t.descripcion}</p>
              {t.archivo_nombre && (
                <a href={`${API}/tareas/${t.id}/archivo`} target="_blank" rel="noreferrer"
                   data-testid={`archivo-tarea-${t.id}`}
                   className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                  <Paperclip className="h-3 w-3" /> {t.archivo_nombre}
                </a>
              )}
              <div className="mt-5 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-primary" /> {t.entregas} de {t.total_estudiantes} entregas
                </span>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => verEntregas(t)}
                        data-testid={`ver-entregas-${t.id}`}>
                  <ClipboardList className="mr-1 h-3 w-3" /> Ver entregas
                </Button>
                {t.enlace && (
                  <a href={t.enlace} target="_blank" rel="noreferrer"
                     data-testid={`abrir-classroom-docente-${t.id}`}
                     className="inline-flex items-center gap-1 rounded-full border border-primary/40 px-4 py-1.5 text-xs font-semibold text-primary lift">
                    Abrir en Classroom
                  </a>
                )}
                {t.origen !== "classroom" && (
                  <Button size="sm" variant="ghost" onClick={() => eliminar(t.id)}
                          data-testid={`eliminar-tarea-${t.id}`}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="modal-tarea-docente">
          <DialogHeader>
            <DialogTitle className="font-display">Nueva actividad</DialogTitle>
            <DialogDescription>Visible en el portal de los padres del grado correspondiente.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label>Materia</Label>
              <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={form.materia_id} onChange={(e) => setForm({ ...form, materia_id: e.target.value })}
                      data-testid="tarea-materia">
                <option value="">Seleccione…</option>
                {materias.map((m) => (
                  <option key={m.id} value={m.id}>{`${m.nombre} — ${m.grado}`}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Título</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                     data-testid="tarea-titulo" />
            </div>
            <div className="space-y-2">
              <Label>Instrucciones</Label>
              <Textarea rows={4} value={form.descripcion} data-testid="tarea-descripcion"
                        onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Fecha de entrega</Label>
              <Input type="date" value={form.fecha_entrega} data-testid="tarea-fecha"
                     onChange={(e) => setForm({ ...form, fecha_entrega: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Archivo adjunto (opcional)</Label>
              <Input type="file" onChange={adjuntar} data-testid="tarea-archivo"
                     accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*" />
              {form.archivo_nombre && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Paperclip className="h-3 w-3" /> {form.archivo_nombre}
                </p>
              )}
            </div>
            <Button className="rounded-full py-6" onClick={crear} data-testid="guardar-tarea-docente">
              Publicar actividad
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="modal-entregas">
          <DialogHeader>
            <DialogTitle className="font-display">{detalle?.tarea?.titulo}</DialogTitle>
            <DialogDescription>Entregas del grado {detalle?.tarea?.grado}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {detalle?.estudiantes?.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-md border border-border/60 p-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{e.nombre}</p>
                  <p className="font-mono text-xs text-muted-foreground">{e.matricula}</p>
                  {e.archivo_nombre && e.entrega_id && (
                    <a href={`${API}/entregas/${e.entrega_id}/archivo`} target="_blank" rel="noreferrer"
                       data-testid={`archivo-entrega-${e.matricula}`}
                       className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                      <Paperclip className="h-3 w-3" /> {e.archivo_nombre}
                    </a>
                  )}
                </div>
                <Badge variant={e.estado === "pendiente" ? "destructive" : e.estado === "evaluada" ? "secondary" : "outline"}>
                  {e.estado}
                </Badge>
                {e.entrega_id && e.estado !== "evaluada" && (
                  <div className="flex items-center gap-2">
                    <Input type="number" min="0" max="100" placeholder="Nota" className="w-20"
                           value={notasEntrega[e.entrega_id] ?? ""}
                           data-testid={`nota-entrega-${e.matricula}`}
                           onChange={(ev) => setNotasEntrega({ ...notasEntrega, [e.entrega_id]: ev.target.value })}
                           onKeyDown={(ev) => { if (ev.key === "Enter") evaluar(e.entrega_id); }} />
                    <Button size="sm" className="rounded-full" data-testid={`evaluar-${e.matricula}`}
                            onClick={() => evaluar(e.entrega_id)}>
                      Evaluar
                    </Button>
                  </div>
                )}
                {e.nota != null && <span className="font-mono text-sm font-semibold">{e.nota}</span>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
